import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from 'react'
import { useSelector } from 'react-redux'
import { getResmgrRoleConfig } from 'openstack/components/infrastructure/actions'
import { uniq } from 'ramda'
import { rawResmgrHostSelector } from '../../infrastructure/selectors'
import useListAction from 'core/hooks/useListAction'
import { listResmgrHosts } from '../../infrastructure/actions'

// Map of hostId -> backend keys (e.g., ["cinder", "glance"]) for that host
export type VolumeBackendsPerHost = Map<string, any[]>
export type VolumeBackendsNamesPerHost = Map<string, string[]>

interface VolumeBackendsContextValue {
  // Map of hostId -> backend objects
  volumeBackendsPerHost: VolumeBackendsPerHost
  // Unique backend keys from all storage hosts (e.g., ["cinder", "glance"])
  usedVolumeBackends: string[]
  // Unique volume_backend_name values from online storage hosts (e.g., ["cinder-nfs", "glance-nfs"])
  activeVolumeBackendNames: string[]
  // Map of hostId -> Unique volume_backend_name values from online storage hosts (e.g., ["cinder-nfs", "glance-nfs"])
  volumeBackendNamesPerHost: VolumeBackendsNamesPerHost
  loading: boolean
  reloadVolumeBackends: () => Promise<void>
}

const VolumeBackendsContext = createContext<VolumeBackendsContextValue | undefined>(undefined)

interface VolumeBackendsProviderProps {
  children: ReactNode
}

/**
 * VolumeBackendsProvider is used to store the volume backend configuration per host.
 *
 * It provides:
 * - volumeBackendsPerHost: Map of hostId to backend objects
 * - usedVolumeBackends: List of all backend keys from all storage hosts (online + offline)
 * - activeVolumeBackendNames: List of volume_backend_name values from online hosts only
 */
export const VolumeBackendsProvider: React.FC<VolumeBackendsProviderProps> = ({ children }) => {
  // Map of hostId -> backend keys (e.g., ["cinder", "glance"])
  const [volumeBackendsPerHost, setVolumeBackendsPerHost] = useState<VolumeBackendsPerHost>(
    new Map(),
  )
  // Backend keys from all hosts (e.g., ["cinder", "glance"])
  const [usedVolumeBackends, setUsedVolumeBackends] = useState<string[]>([])
  // volume_backend_name values from online hosts (e.g., ["cinder-nfs", "glance-nfs"])
  const [activeVolumeBackendNames, setActiveVolumeBackendNames] = useState<string[]>([])
  // Store volume_backend_name per host for online calculation
  const [volumeBackendNamesPerHost, setVolumeBackendNamesPerHost] = useState<
    VolumeBackendsNamesPerHost
  >(new Map())
  const [loading, setLoading] = useState(true)
  const [hasFetched, setHasFetched] = useState(false)

  // Load all resmgr host without any dependency
  const { loading: loadingHosts } = useListAction(listResmgrHosts, { ignoreDependency: false })
  const hosts = useSelector(rawResmgrHostSelector)

  // Helper to recalculate derived state
  const recalculateDerivedState = useCallback(
    (
      perHostMap: VolumeBackendsPerHost,
      namesPerHost: Map<string, string[]>,
      onlineIds: Set<string>,
    ) => {
      const usedBackendKeys: string[] = []
      const activeVolumeBackendNamesLoc: string[] = []

      // Collect all backend keys from all hosts
      perHostMap.forEach((backends) => {
        usedBackendKeys.push(...Object.keys(backends))
      })

      // Collect volume_backend_name only from online hosts
      namesPerHost.forEach((names, hostId) => {
        if (onlineIds.has(hostId)) {
          activeVolumeBackendNamesLoc.push(...names)
        }
      })

      setUsedVolumeBackends(uniq(usedBackendKeys))
      setActiveVolumeBackendNames(uniq(activeVolumeBackendNamesLoc))
    },
    [],
  )

  const fetchVolumeBackends = useCallback(
    async (hostsList: any[]) => {
      if (hostsList.length === 0) {
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const storageHosts = hostsList.filter((host) => host.roles.includes('persistent-storage'))
        if (storageHosts.length === 0) {
          setLoading(false)
          setHasFetched(true)
          return
        }

        const onlineStorageHosts = storageHosts.filter((host) => host?.info?.responding)
        const onlineIds = new Set(onlineStorageHosts.map((host) => host.id))

        // Fetch backends for ALL storage hosts (not just online)
        const results = await Promise.allSettled(
          storageHosts.map((host) =>
            getResmgrRoleConfig({ role: 'pf9-cindervolume-config', host: host }),
          ),
        )

        // Build the per-host maps
        // perHostMap: hostId -> backend keys (e.g., ["cinder", "glance"])
        // namesPerHost: hostId -> volume_backend_name values (e.g., ["cinder-nfs", "glance-nfs"])
        const perHostMap = new Map<string, string[]>()
        const namesPerHost = new Map<string, string[]>()

        storageHosts.forEach((host, idx) => {
          const result = results[idx] as any
          if (result?.value?.backends) {
            perHostMap.set(host.id, result.value.backends)

            // Extract volume_backend_name values (e.g., "cinder-nfs", "glance-nfs")
            const backendNames = Object.values(result.value.backends)
              .map((backend: any) => backend.volume_backend_name)
              .filter(Boolean) as string[]
            namesPerHost.set(host.id, uniq(backendNames))
          } else {
            perHostMap.set(host.id, [])
            namesPerHost.set(host.id, [])
          }
        })

        setVolumeBackendsPerHost(perHostMap)
        setVolumeBackendNamesPerHost(namesPerHost)
        recalculateDerivedState(perHostMap, namesPerHost, onlineIds)
        setHasFetched(true)
      } catch (error) {
        console.error('Failed to fetch volume backends:', error)
      } finally {
        setLoading(false)
      }
    },
    [recalculateDerivedState],
  )

  useEffect(() => {
    if (!loadingHosts && hosts.length > 0 && !hasFetched) {
      fetchVolumeBackends(hosts)
    } else if (!loadingHosts && hosts.length === 0) {
      setLoading(false)
    }
  }, [hosts?.length, loadingHosts, hasFetched, fetchVolumeBackends])

  const reloadVolumeBackends = useCallback(async () => {
    setHasFetched(false)
    await fetchVolumeBackends(hosts)
  }, [hosts, fetchVolumeBackends])

  const value: VolumeBackendsContextValue = {
    volumeBackendsPerHost,
    usedVolumeBackends,
    activeVolumeBackendNames,
    volumeBackendNamesPerHost,
    loading: loading || loadingHosts,
    reloadVolumeBackends,
  }

  return <VolumeBackendsContext.Provider value={value}>{children}</VolumeBackendsContext.Provider>
}

/**
 * Hook to access cached volume backends from context
 * Must be used within a VolumeBackendsProvider
 */
export const useVolumeBackends = (): VolumeBackendsContextValue => {
  const context = useContext(VolumeBackendsContext)
  if (!context) {
    throw new Error('useVolumeBackends must be used within a VolumeBackendsProvider')
  }
  return context
}
